import { FC, useEffect, useMemo, useCallback, useState } from 'react';
import { MapPin, Plus, Menu } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { navigateTo } from '../store/slices/navigationSlice';
import { fetchActiveVisit, fetchVisitsLimited, selectVisitsList, selectActiveVisitLoading, selectHistoryLoading, setActiveVisit } from '../store/slices/visitsSlice';
import { fetchCustomersLimited, selectCustomersList, selectCustomersDataLoading, selectTotalCustomerCount, selectTotalCustomerCountLoading, fetchTotalCustomerCount } from '../store/slices/customersSlice';
import { fetchActivityInteractions, selectTodayInteractionsLoading, selectActiveInteractions, selectActiveInteractionsCount, CustomerInteractionRecord, updateCustomerInteraction } from '../store/slices/interactionsSlice';
import { fetchEmployeeStats, fetchEmployeeProfile, selectProfileLoading, selectStatsLoading, selectTelegramUser } from '../store/slices/authSlice';
import { BottomNavigation } from '../components/ui/BottomNavigation';
import { Sidebar } from '../components/ui/Sidebar';
import { NewCustomerModal } from '../components/NewCustomerModal';
import { CustomerDetailModal } from '../components/CustomerDetailModal';
import { InteractionDetailModal } from '../components/interactions/InteractionDetailModal';
import { InstantVisitModal } from '../components/ui/InstantVisitModal';
import { DynamicCalendarIcon } from '../components/ui/DynamicCalendarIcon';
import { InteractionScheduleItem } from '../components/ui/InteractionScheduleItem';
import { getWIBTimeISO } from '../lib/timeUtils';
import { safeHapticImpact, safeHapticNotification } from '../lib/telegramUtils';
import { Customer, ProductPromotion } from '../lib/types';
import { determineInteractionStatus } from '../lib/interactionStatusUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { useTelegramWebApp } from '../providers/TelegramWebAppProvider';
import { Button } from '../components/ui/button';
import { AdCarousel, Ad } from '../components/ui/AdCarousel';
import {
  fetchProductPromotions,
  selectActivePromotionsSorted,
  selectProductPromotionsLoading
} from '../store/slices/productPromotionsSlice';
import {
  navigateToWithTab
} from '../store/slices/navigationSlice';
import {
  fetchProducts,
  selectProductsList,
  selectProductsLoading
} from '../store/slices/productsSlice';

export const Dashboard: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  useTelegramWebApp();
  const { employee, dailyStats } = useAppSelector((state) => state.auth);
  const telegramUser = useAppSelector(selectTelegramUser);
  const currentScreen = useAppSelector((state) => state.navigation.currentScreen);
  const visits = useAppSelector(selectVisitsList);
  useAppSelector(selectCustomersList);
  useAppSelector(selectTotalCustomerCount);
  useAppSelector(selectTotalCustomerCountLoading);
  const activityInteractions = useAppSelector(selectActiveInteractions); // Only shows non-completed interactions
  useAppSelector(selectActiveInteractionsCount); // Count for active (non-completed) activities
  const productPromotions = useAppSelector(selectActivePromotionsSorted) as ProductPromotion[];
  useAppSelector(selectProductPromotionsLoading);
  const productsList = useAppSelector(selectProductsList);
  useAppSelector(selectProductsLoading);
  
  // Filter interactions to match exactly what Interactions Tab > Scheduled shows (pending status only)
  const scheduledInteractions = useMemo(() => {
    return activityInteractions.filter(interaction => {
      const status = determineInteractionStatus(interaction.date_created, interaction.done);
      return status.status === 'pending'; // Only show pending interactions, exclude overdue
    });
  }, [activityInteractions]);
  
  const scheduledInteractionsCount = scheduledInteractions.length;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<CustomerInteractionRecord | null>(null);
  const [showCustomerDetailModal, setShowCustomerDetailModal] = useState(false);
  const [showInteractionDetailModal, setShowInteractionDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Granular loading states
  const profileLoading = useAppSelector(selectProfileLoading);
  useAppSelector(selectActiveVisitLoading);
  useAppSelector(selectHistoryLoading);
  const interactionsLoading = useAppSelector(selectTodayInteractionsLoading);
  useAppSelector(selectCustomersDataLoading);
  useAppSelector(selectStatsLoading);

  // Lazy loading strategy - only load data when dashboard is active
  useEffect(() => {
    // Only load data if we're actually on the dashboard screen
    if (currentScreen !== 'dashboard') {
      if (import.meta.env.DEV) {
        console.log('🚫 Dashboard: Skipping data load, not on dashboard screen:', currentScreen);
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.log('✅ Dashboard: Loading data for active dashboard screen');
    }

    // Phase 1: Critical data (parallel execution for immediate dashboard functionality)
    const loadCriticalData = async () => {
      const criticalResults = await Promise.allSettled([
        dispatch(fetchEmployeeProfile()),
        dispatch(fetchActiveVisit()),
        dispatch(fetchActivityInteractions())
      ]);

      // Reduced logging - only log failures in dev mode
      if (import.meta.env.DEV) {
        criticalResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            const operations = ['fetchEmployeeProfile', 'fetchActiveVisit', 'fetchActivityInteractions'];
            console.warn(`Critical data load failed for ${operations[index]}:`, result.reason);
          }
        });
      }
    };

    // Phase 2: Secondary data (background loading with slight delay to prioritize critical data)
    const loadSecondaryData = () => {
      setTimeout(() => {
        Promise.allSettled([
          dispatch(fetchVisitsLimited()), // Limited visits for performance
          dispatch(fetchCustomersLimited()), // Limited customers for dashboard
          dispatch(fetchTotalCustomerCount()), // Total customer count from Data_Customer table
          dispatch(fetchEmployeeStats(false)),
          dispatch(fetchProductPromotions()), // Load product promotions for carousel
          dispatch(fetchProducts()) // Load products for carousel images
        ]).then((secondaryResults) => {
          // Reduced logging - only log failures in dev mode
          if (import.meta.env.DEV) {
            secondaryResults.forEach((result, index) => {
              if (result.status === 'rejected') {
                const operations = ['fetchVisitsLimited', 'fetchCustomersLimited', 'fetchTotalCustomerCount', 'fetchEmployeeStats', 'fetchProductPromotions', 'fetchProducts'];
                console.info(`Secondary data load failed for ${operations[index]}:`, result.reason);
              }
            });
          }
        });
      }, 100); // Small delay to prioritize critical data
    };

    // Execute both phases
    loadCriticalData();
    loadSecondaryData();
  }, [dispatch, currentScreen]);

  // 🔄 AGGRESSIVE AUTO-RELOAD: Refresh Activity Interactions immediately on ANY interruption
  // Triggers: app from background, modal close, tab/screen change back to dashboard
  useEffect(() => {
    if (currentScreen !== 'dashboard') {
      return; // Only active when dashboard is the current screen
    }

    // 1️⃣ VISIBILITY CHANGE: Refresh when app returns from background
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - reload activity interactions IMMEDIATELY
        if (import.meta.env.DEV) {
          console.log('👁️ Dashboard visible again - reloading Activity Interactions');
        }
        dispatch(fetchActivityInteractions());
      }
    };

    // 2️⃣ FOCUS EVENT: Refresh when window/tab gains focus
    const handleFocus = () => {
      if (import.meta.env.DEV) {
        console.log('🎯 Dashboard gained focus - reloading Activity Interactions');
      }
      dispatch(fetchActivityInteractions());
    };

    // Listen for visibility changes (tab switches, app reopening)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch, currentScreen]);

  // 3️⃣ NAVIGATION-BASED RELOAD: Refresh when returning to Dashboard from other screens
  useEffect(() => {
    // This useEffect triggers whenever currentScreen changes
    // If we just navigated TO dashboard, reload interactions immediately
    if (currentScreen === 'dashboard') {
      if (import.meta.env.DEV) {
        console.log('🔄 Navigated to Dashboard - reloading Activity Interactions');
      }
      dispatch(fetchActivityInteractions());
    }
  }, [currentScreen, dispatch]);

  // Stats are calculated in useMemo below - no need for separate effect

  // Calculate real statistics from Directus data - optimized single iteration
  const realStats = useMemo(() => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const weekStart = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));
    
    // Single iteration over visits array (js-7.6: Combine Multiple Array Iterations)
    let todayTotal = 0;
    let todayCompleted = 0;
    let weekTotal = 0;
    let weekCompleted = 0;
    
    for (const visit of visits) {
      const visitDate = new Date(visit.start_time);
      const isCompleted = visit.status === 'completed';
      
      if (visitDate >= weekStart) {
        weekTotal++;
        if (isCompleted) weekCompleted++;
        
        if (visitDate >= todayStart) {
          todayTotal++;
          if (isCompleted) todayCompleted++;
        }
      }
    }
    
    // Reduced logging for performance
    if (import.meta.env.DEV && visits.length > 0) {
      console.log(`📊 Dashboard Stats: ${visits.length} visits, week: ${weekCompleted}/${weekTotal}`);
    }
    
    return {
      todayTotal,
      todayCompleted,
      weekTotal,
      weekCompleted,
      avgRating: dailyStats?.avgRating || 4.5,
    };
  }, [visits, dailyStats?.avgRating]);

  // Schedule is now loaded from Redux via fetchTodaySchedule

  const [showInstantVisitModal, setShowInstantVisitModal] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  // Placeholder image URL for promotions without product image
  const PLACEHOLDER_IMAGE = 'https://placehold.co/600x400/1e293b/94a3b8?text=Promotion';

  // Convert product promotions to ads format for carousel with product images
  const promotionAds: Ad[] = useMemo(() => {
    // Helper function to match product name (partial match, case insensitive)
    const findMatchingProduct = (promotionProductName: string) => {
      if (!promotionProductName || !productsList || productsList.length === 0) {
        return null;
      }

      const searchTerm = promotionProductName.toLowerCase().trim();

      // Find first product where product name contains the search term OR search term contains the product name
      return productsList.find((product: any) => {
        const productName = (product.product_name || product.name || '').toLowerCase().trim();
        if (!productName) return false;

        // Partial match: either direction
        return productName.includes(searchTerm) || searchTerm.includes(productName);
      });
    };

    return productPromotions.map((promo) => {
      // Priority level colors
      // Priority level colors using CSS custom properties for theme consistency
      const colors: Record<string, string> = {
        uv_ivs: 'rgb(var(--status-info))', // blue
        tomato_b3: 'rgb(var(--status-success))', // green
        tomato_s3: 'rgb(var(--status-warning))', // amber
        tomato_s3v: 'hsl(var(--chart-2))', // violet (using chart color)
        tomato_s3t: 'rgb(var(--status-error))', // red
        aqua_500kg: 'hsl(var(--chart-2))', // cyan (using chart color)
        aqua_2ton: 'rgb(var(--status-success))', // lime (using success)
        gropro_116: 'rgb(var(--status-warning))', // orange (using warning)
        yeguides_100: 'hsl(var(--chart-2))', // pink (using chart color)
        tanning_104: 'hsl(var(--chart-4))', // indigo (using chart color)
        garlic: 'rgb(var(--status-success))', // teal (using success)
        oxy_powder: 'rgb(var(--status-neutral))', // slate (using neutral)
        feeding: 'rgb(var(--status-neutral))' // slate (using neutral)
      };

      // Format commission amount
      const formatCommission = (amount: number, unit: string) => {
        const formattedAmount = new Intl.NumberFormat('id-ID').format(amount);
        switch (unit) {
          case 'flat_rate':
            return `Rp ${formattedAmount}`;
          case 'per_product':
            return `Rp ${formattedAmount}/produk`;
          case 'per_pail':
            return `Rp ${formattedAmount}/pail`;
          case 'per_unit':
            return `Rp ${formattedAmount}/unit`;
          default:
            return `Rp ${formattedAmount}`;
        }
      };

      // Try to find matching product and get image
      const matchingProduct = findMatchingProduct(promo.product_name);
      const productImageUrl = matchingProduct?.cover_image_url || null;

      // Use product image if available, otherwise use placeholder
      const imageUrl = productImageUrl || PLACEHOLDER_IMAGE;

      if (import.meta.env.DEV && matchingProduct) {
        console.log(`✅ Matched promotion "${promo.product_name}" with product "${matchingProduct.product_name}"`, {
          hasImage: !!productImageUrl,
          imageUrl
        });
      }

      return {
        id: promo.id.toString(),
        title: promo.product_name,
        description: `${formatCommission(promo.commission_amount, promo.commission_unit)}${promo.management_amount ? ` + Management Rp ${new Intl.NumberFormat('id-ID').format(promo.management_amount)}` : ''}${promo.description ? ` - ${promo.description}` : ''}`,
        background_color: colors[promo.priority_level] || '#64748b',
        image_url: imageUrl, // Add product image
        link: '#', // Internal navigation, not external
        promoId: promo.id // Store promotion ID for navigation
      };
    });
  }, [productPromotions, productsList]);

  // Reset profile image error when photo URL changes
  useEffect(() => {
    if (telegramUser?.photo_url) {
      setProfileImageError(false);
    }
  }, [telegramUser?.photo_url]);

  const handleStartVisit = () => {
    setShowInstantVisitModal(true);
  };

  const handleStartVisitFromCustomer = useCallback(async (customer: Customer) => {
    try {
      // Reduced logging for performance
      if (import.meta.env.DEV) {
        console.log('🎯 Starting visit for customer:', customer.name || customer.id);
      }
      
      // Close the customer detail modal first
      setShowCustomerDetailModal(false);
      setSelectedCustomer(null);
      
      // Safe haptic feedback
      safeHapticImpact('light');

      // FIXED: Create LOCAL visit only (like CustomerSelection.tsx), NO direct Directus API call
      // Get GPS coordinates with fallback to customer coordinates
      let coordinates = { 
        lat: customer.latitude || -6.2088, // Default to Jakarta center if no coordinates
        lng: customer.longitude || 106.8456 
      };
      
      // Try to get current GPS position (similar to CustomerSelection logic)
      const hasValidCoordinates = customer.latitude && customer.longitude;
      if (!hasValidCoordinates) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 60000
            });
          });
          
          coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          if (import.meta.env.DEV) {
            console.log('🌍 GPS captured:', position.coords.latitude, position.coords.longitude);
          }
          
        } catch (gpsError) {
          console.warn('⚠️ GPS not available, using customer or default coordinates:', gpsError);
        }
      }

      // Create LOCAL visit object (like CustomerSelection.tsx - NO API call yet)
      // IMPORTANT: Include interaction tracking fields for later completion
      const localVisit = {
        id: `temp_activity_${Date.now()}`, // Temporary ID
        employee_id: employee?.id || '',
        customer_id: customer.id || parseInt(customer.idcust || '0'),
        customer_name: customer.name || customer.NamaPelangganTambak || 'Unknown Customer',
        customer_address: customer.address || customer.Alamat || 'No address provided',
        start_time: getWIBTimeISO(),
        status: 'active' as const,
        notes: `Visit started from Activity Interactions on ${new Date().toLocaleString()}`,
        satisfaction_rating: 0,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        visit_coordinates: coordinates,
        customer_tambak_pelanggan: customer.idcust || customer.id,
        customer_data: customer, // Include full customer data for later use
        visit_purpose: 'Customer interaction follow-up',
        
        // CRITICAL: Fields needed for ActiveVisit.tsx to update interaction on completion
        isFromInteraction: true, // Flag that this visit came from interaction
        original_interaction_id: selectedInteraction?.id, // The interaction ID to update when visit completes
        original_interaction_data: selectedInteraction, // Full interaction data for reference
        
        isLocalOnly: true, // Flag that this hasn't been sent to Directus yet
        created_at: getWIBTimeISO(),
        updated_at: getWIBTimeISO()
      };

      if (import.meta.env.DEV) {
        console.log('📋 Creating LOCAL visit for:', customer.name);
      }
      
      // Set as active visit (LOCAL state only - like CustomerSelection)
      dispatch(setActiveVisit(localVisit));
      
      // Navigate to Visit in Progress screen immediately
      dispatch(navigateTo('visitActive'));
      
      // Safe haptic feedback for success
      safeHapticNotification('success');
      
      if (import.meta.env.DEV) {
        console.log('✅ Local visit set, navigating to visitActive');
      }
      
    } catch (error) {
      console.error('Error starting local visit from Activity Interactions:', error);
      
      // Safe haptic feedback for error
      safeHapticNotification('error');
      
      // Fallback to customer selection screen
      dispatch(navigateTo('interactions'));
    }
  }, [dispatch, employee, selectedInteraction]);

  const handleMenuClick = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Convert CustomerInteractionRecord to Customer format for CustomerDetailModal
  useCallback((interaction: CustomerInteractionRecord): Customer => {
    // Try multiple sources for customer name to avoid "Unknown Customer"
    const customerName = interaction.customer_name || 
                        interaction.tambak_name || 
                        interaction.No_pelanggan || 
                        'Unknown Customer';
    
    return {
      id: interaction.id,
      name: customerName,
      NamaPelangganTambak: customerName,
      address: '', // We don't have address in interaction
      Alamat: '',
      contact_person: interaction.pic_name || undefined,
      phone: '', // We don't have phone in interaction
      Contacs: '',
      priority: 'medium' as const,
      status: 'active' as const,
      last_visit: interaction.date_created,
      created_at: interaction.date_created,
      updated_at: interaction.date_created,
      idcust: interaction.No_pelanggan,
      customer_tambak_id: interaction.customer_tambak_id,
      PIC: interaction.pic_name,
      // Optional fields with defaults
      ukuranTambak: undefined,
      tahunDibangun: undefined,
      tglPembelianTerakhir: undefined,
      tglInteraksiTerakhir: interaction.date_created,
      source: undefined,
      SIC: undefined,
      Description: interaction.keterangan,
      statusAktif: 1,
      farmIsActive: true,
      script: undefined,
      approval_status: 'approved' as const,
      approval_date: undefined,
      approved_by: undefined,
      approval_notes: undefined,
      revenue: undefined,
      distance: undefined,
      lastInteraction: interaction.date_created,
      latitude: undefined,
      longitude: undefined,
      tambak_name: interaction.customer_name,
      tambak_size: undefined,
      year_built: undefined,
      last_purchase_date: undefined,
      lead_source: undefined,
      assigned_pic: interaction.pic_name,
      pic_id: undefined
    };
  }, []);

  // Convert CustomerInteractionRecord to format expected by InteractionDetailModal
  const convertInteractionToModalFormat = useCallback((interaction: CustomerInteractionRecord) => {
    const status = determineInteractionStatus(interaction.date_created, interaction.done);
    return {
      ...interaction,
      subject: interaction.keterangan || interaction.interaction_type || 'No subject',
      status: status.status as 'completed' | 'scheduled' | 'pending' | 'cancelled',
      priority: interaction.priority || 'medium' as 'low' | 'medium' | 'high',
      follow_up_required: false,
      done: interaction.done, // FIXED: Preserve original done value instead of defaulting to false
      // FIXED: Add PIC field that InteractionDetailModal expects
      PIC: interaction.pic_name || interaction.PIC || 'Unknown PIC'
    };
  }, []);

  const handleInteractionClick = useCallback((interaction: CustomerInteractionRecord) => {
    // Safe haptic feedback
    safeHapticImpact('light');
    
    // Set selected interaction directly (like Interactions Tab)
    setSelectedInteraction(interaction);
    setShowInteractionDetailModal(true);
    
    // Reduced logging for performance
    if (import.meta.env.DEV) {
      console.log('🔍 Opening interaction:', interaction.customer_name);
    }
  }, []);

  // Interaction Detail Modal handlers (consistent with Interactions Tab)
  const handleUpdateInteraction = useCallback((updatedInteraction: any) => {
    dispatch(updateCustomerInteraction({
      id: updatedInteraction.id,
      data: {
        keterangan: updatedInteraction.keterangan,
        interaction_type: updatedInteraction.interaction_type,
        customer_tambak_id: updatedInteraction.customer_tambak_id,
        visit_id: updatedInteraction.visit_id,
        date_remind: updatedInteraction.date_remind,
        priority: updatedInteraction.priority,
      }
    }));
  }, [dispatch]);

  const handleCompleteInteraction = useCallback((completedInteraction: any) => {
    dispatch(updateCustomerInteraction({
      id: completedInteraction.id,
      data: {
        ...completedInteraction,
        done: true, // Mark as completed using the done field
        keterangan: (completedInteraction.keterangan || '') + ' [COMPLETED]'
      }
    }));
  }, [dispatch]);

  const handleRescheduleInteraction = useCallback((rescheduledInteraction: any) => {
    dispatch(updateCustomerInteraction({
      id: rescheduledInteraction.id,
      data: {
        date_remind: rescheduledInteraction.date_remind,
        priority: rescheduledInteraction.priority,
        keterangan: rescheduledInteraction.keterangan,
        interaction_type: rescheduledInteraction.interaction_type
      }
    }));
  }, [dispatch]);

  const handleCreateFollowUp = useCallback((originalInteraction: any) => {
    // For now, just close the modal - could implement follow-up creation later
    setShowInteractionDetailModal(false);
    setSelectedInteraction(null);
    if (import.meta.env.DEV) {
      console.log('Follow-up creation for:', originalInteraction.customer_name);
    }
  }, []);

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const getCurrentDate = () => {
    const date = new Date();
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const day = date.toLocaleDateString('en-US', { day: '2-digit' });
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.toLocaleDateString('en-US', { year: 'numeric' });
    
    return `${weekday}, ${day} ${month} ${year}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return t('dashboard.greeting.morning');
    } else if (hour >= 12 && hour < 17) {
      return t('dashboard.greeting.afternoon');
    } else if (hour >= 17 && hour < 21) {
      return t('dashboard.greeting.evening');
    } else {
      return t('dashboard.greeting.night');
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header with User Info */}
      <div className="bg-card border-b border-border p-4 shadow-sm">
        <div className="text-center mb-2">
          <h1 className="text-lg font-semibold text-foreground">{t('dashboard.title')}</h1>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {telegramUser?.photo_url && !profileImageError ? (
              <img
                src={telegramUser.photo_url}
                alt={`${employee?.first_name || employee?.full_name?.split(' ')[0] || 'User'} profile`}
                className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                onError={() => setProfileImageError(true)}
                onLoad={() => setProfileImageError(false)}
              />
            ) : (
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                {employee?.first_name?.charAt(0) || employee?.full_name?.charAt(0) || 'J'}
              </div>
            )}
            <div data-testid="user-greeting">
              {profileLoading ? (
                <div className="animate-pulse">
                  <div className="h-5 bg-muted rounded w-3/4 mb-1"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              ) : (
                <>
                  <h1 className="font-bold text-foreground">{getGreeting()}, {employee?.first_name || employee?.full_name?.split(' ')[0] || 'John'}!</h1>
                  <p className="text-sm text-muted-foreground">{getCurrentTime()} {getCurrentDate()}</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Full-screen toggle button - only show in Telegram */}
            {/* {isTelegram && (
              <Button
                variant="outline"
                size="sm"
                onClick={isFullscreen ? exitFullscreen : requestFullscreen}
                title={isFullscreen ? t('common.fullscreen.exit') : t('common.fullscreen.enter')}
              >
                {isFullscreen ?
                  <Minimize className="w-5 h-5" /> :
                  <Maximize className="w-5 h-5" />
                }
              </Button>
            )} */}
            <Button variant="outline" size="sm" onClick={handleMenuClick}>
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>

      </div>

      {/* Quick Actions */}
      <div className="p-4 flex-1 pb-24">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={handleStartVisit}
            className="bg-primary text-primary-foreground p-4 rounded-xl flex flex-col items-center space-y-2 hover:bg-primary/90 transition-colors"
            data-testid="start-visit-btn"
          >
            <MapPin className="w-8 h-8" />
            <span className="font-medium">{t('dashboard.actions.instant_visit')}</span>
          </button>
          
          <button 
            onClick={() => setShowNewCustomerModal(true)}
            className="bg-secondary text-secondary-foreground p-4 rounded-xl flex flex-col items-center space-y-2 hover:bg-accent transition-colors" 
            data-testid="new-customer-btn"
          >
            <Plus className="w-8 h-8" />
            <span className="font-medium">{t('dashboard.actions.new_customer')}</span>
          </button>
        </div>

        {/* Activity Interactions Card */}
        <div className="bg-card rounded-xl p-4 mb-4 shadow-sm" data-testid="activity-interactions">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center space-x-2">
              <DynamicCalendarIcon 
                size="medium" 
                color="primary"
                onClick={() => {
                  // Future: Navigate to interactions view or show date picker
                  dispatch(navigateTo('interactions'));
                }}
              />
              <h3 className="font-semibold">{t('dashboard.activity.title')}</h3>
            </div>
            <span className="text-sm text-muted-foreground">{scheduledInteractionsCount} {t('dashboard.activity.scheduled')}</span>
          </div>
          
          {interactionsLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">{t('dashboard.activity.loading')}</span>
            </div>
          ) : scheduledInteractions.length > 0 ? (
            <div className="space-y-2">
              {scheduledInteractions.map((interaction) => (
                <InteractionScheduleItem
                  key={interaction.id}
                  interaction={interaction}
                  onClick={(interaction) => {
                    handleInteractionClick(interaction);
                  }}
                  className="bg-muted border-border"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-3">
                <DynamicCalendarIcon 
                  size="large" 
                  color="blue"
                  className="opacity-50"
                />
              </div>
              <p className="text-muted-foreground text-sm">{t('dashboard.activity.empty_message')}</p>
              <p className="text-muted-foreground text-xs mt-1">{t('dashboard.activity.empty_subtitle')}</p>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 gap-3" data-testid="stats-grid">
          <div className="bg-card p-3 rounded-lg text-center shadow-sm">
            <div className="text-lg font-bold text-primary">{realStats.weekCompleted}</div>
            <div className="text-xs text-muted-foreground">{t('dashboard.stats.this_week')}</div>
          </div>
          {/* Hidden: Avg Rating and Total Customers stats */}
        </div>

        {/* Product Promotions Carousel */}
        <div className="mt-4">
          <AdCarousel 
            ads={promotionAds} 
            autoPlayInterval={5000}
            onAdClick={(_ad) => {
              // Navigate to products screen with promotions tab active
              dispatch(navigateToWithTab({ screen: 'products', tab: 'promotions' }));
            }}
          />
        </div>

      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />

      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      
      {/* New Customer Modal */}
      <NewCustomerModal
        isOpen={showNewCustomerModal}
        onClose={() => {
          setShowNewCustomerModal(false);

          // 4️⃣ MODAL CLOSE RELOAD: Refresh interactions after modal closes
          if (import.meta.env.DEV) {
            console.log('🔄 NewCustomerModal closed - reloading Activity Interactions');
          }
          dispatch(fetchActivityInteractions());
        }}
        onSuccess={() => {
          // Refresh customer data and stats after successful creation
          dispatch(fetchCustomersLimited());
          dispatch(fetchEmployeeStats(false));
          dispatch(fetchActivityInteractions()); // Also refresh interactions
        }}
      />

      {/* Customer Detail Modal for Activity Interactions */}
      <CustomerDetailModal
        customer={selectedCustomer}
        isOpen={showCustomerDetailModal}
        onClose={() => {
          setShowCustomerDetailModal(false);
          setSelectedCustomer(null);
          setSelectedInteraction(null);

          // 4️⃣ MODAL CLOSE RELOAD: Refresh interactions after modal closes
          if (import.meta.env.DEV) {
            console.log('🔄 CustomerDetailModal closed - reloading Activity Interactions');
          }
          dispatch(fetchActivityInteractions());
        }}
        onStartVisit={handleStartVisitFromCustomer}
      />

      {/* Interaction Detail Modal - Consistent with Interactions Tab */}
      <InteractionDetailModal
        isOpen={showInteractionDetailModal}
        onClose={() => {
          setShowInteractionDetailModal(false);
          setSelectedInteraction(null);

          // 4️⃣ MODAL CLOSE RELOAD: Refresh interactions after modal closes
          if (import.meta.env.DEV) {
            console.log('🔄 InteractionDetailModal closed - reloading Activity Interactions');
          }
          dispatch(fetchActivityInteractions());
        }}
        interaction={selectedInteraction ? convertInteractionToModalFormat(selectedInteraction) : null}
        onUpdate={handleUpdateInteraction}
        onComplete={handleCompleteInteraction}
        onReschedule={handleRescheduleInteraction}
        onCreateFollowUp={handleCreateFollowUp}
      />

      {/* Instant Visit Modal */}
      <InstantVisitModal
        isOpen={showInstantVisitModal}
        onClose={() => {
          setShowInstantVisitModal(false);

          // 4️⃣ MODAL CLOSE RELOAD: Refresh interactions after modal closes
          if (import.meta.env.DEV) {
            console.log('🔄 InstantVisitModal closed - reloading Activity Interactions');
          }
          dispatch(fetchActivityInteractions());
        }}
        onVisitCreated={() => dispatch(navigateTo('visitActive'))}
      />
    </div>
  );
};